/**
 * Projects — Proje Yönetim Sistemi (PYS) liste sayfası.
 *
 * Server-side pagination'lı proje listesi; arama + durum filtresi,
 * satır tıklamada proje detayına gider (/projeler/:id — Plan 3),
 * üç nokta menüsünde Düzenle / Sil / İşlem Geçmişi bulunur.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { useAuth } from '../../../contexts/AuthContext';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import RecordHistoryModal from '../../common/RecordHistoryModal';
import AddProjectModal from './AddProjectModal';
import GuideButton from '../../common/guide/GuideButton';
import {
  getProjectsPaged,
  deleteProject,
  updateProjectStatus,
  type Project,
  type ProjectStatus,
} from '../../../services/projectService';

/** İşlem Geçmişi modalındaki alan adı → Türkçe etiket eşlemesi. */
export const PROJECT_HISTORY_FIELD_LABELS: Record<string, string> = {
  Name: 'Proje Adı',
  Code: 'Proje Kodu',
  Status: 'Durum',
  AccountId: 'Cari Hesap',
  Description: 'Açıklama',
  StartDate: 'Başlangıç Tarihi',
  EndDate: 'Bitiş Tarihi',
  CreatedBy: 'Oluşturan',
  IsActive: 'Aktif mi',
  IsDeleted: 'Silindi mi',
};

const Projects: React.FC = () => {
  const { t, i18n } = useTranslation('common2');
  const navigate = useNavigate();
  const { userDetails } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const localeCode = i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US';

  // Liste state'leri (server-side pagination)
  const [projects, setProjects] = useState<Project[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0); // 0-based
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtreler
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cari adı eşlemesi (AccountId → ad)
  const [accountNames, setAccountNames] = useState<Record<number, string>>({});

  // Satır menüsü + modallar
  const [anchorElRow, setAnchorElRow] = useState<{ [key: number]: HTMLElement | null }>({});
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<Project | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ project: Project; status: ProjectStatus } | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const loadProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getProjectsPaged({
        page: page + 1,
        pageSize,
        search: search || undefined,
        status: statusFilter || undefined,
        includeSummary: true,
      });
      setProjects(result.data);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, statusFilter]);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Cari adları (kolon gösterimi için — hafif tek istek)
  useEffect(() => {
    const loadAccounts = async () => {
      try {
        const response = await accountingApi.get(ACCOUNTING_API.ACCOUNT_BY_COMPANY);
        const accountsData = response.data?.data || response.data?.Data || response.data;
        if (Array.isArray(accountsData)) {
          const map: Record<number, string> = {};
          accountsData.forEach((acc: any) => {
            const id = acc.Id || acc.id;
            const name = acc.CompanyName || acc.companyName || acc.Name || acc.name;
            if (id && name) map[id] = name;
          });
          setAccountNames(map);
        }
      } catch (err) {
        console.error('[Projects] Cari hesaplar yüklenemedi:', err);
      }
    };
    loadAccounts();
  }, []);

  // Arama debounce (500ms)
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setPage(0);
      setSearch(value.trim());
    }, 500);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await deleteProject(deleteTarget.id);
      setDeleteTarget(null);
      setSnackbar(t('projects.deleted'));
      loadProjects();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusChange = async () => {
    if (!statusTarget) return;
    try {
      setStatusSaving(true);
      await updateProjectStatus(statusTarget.project.id, statusTarget.status);
      setStatusTarget(null);
      setSnackbar(t('projects.statusChanged'));
      loadProjects();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
      setStatusTarget(null);
    } finally {
      setStatusSaving(false);
    }
  };

  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(localeCode) : '-');

  const fmtMoney = useCallback(
    (value: number) =>
      new Intl.NumberFormat(localeCode, {
        style: 'currency',
        currency: 'TRY',
        maximumFractionDigits: 2,
      }).format(value),
    [localeCode],
  );

  /** Özet hücresi: özet gelmediyse "-" gösterir (ör. eski API sürümü). */
  const moneyCell = useCallback(
    (value: number | undefined, color?: string) =>
      value === undefined ? (
        <Typography variant="body2" color="text.disabled">-</Typography>
      ) : (
        <Typography variant="body2" fontWeight={600} sx={{ color: color || 'text.primary' }}>
          {fmtMoney(value)}
        </Typography>
      ),
    [fmtMoney],
  );

  const statusChip = (status: string) => {
    const map: Record<string, { color: 'primary' | 'success' | 'default'; label: string }> = {
      active: { color: 'primary', label: t('projects.statusActive') },
      completed: { color: 'success', label: t('projects.statusCompleted') },
      cancelled: { color: 'default', label: t('projects.statusCancelled') },
    };
    const cfg = map[status] || { color: 'default' as const, label: status };
    return <Chip size="small" color={cfg.color} label={cfg.label} />;
  };

  const closeRowMenu = (id: number) => setAnchorElRow((prev) => ({ ...prev, [id]: null }));

  /**
   * Menü, satırın içinde render edildiği için tıklama React ağacında satıra kadar
   * çıkar ve satırın onClick'i (detaya git) tetiklenir; bu sarmalayıcı olayı
   * durdurur. Aksi halde "Sil" gibi modal açan aksiyonlar sayfa değiştiği için
   * hiç görünmez.
   */
  const rowMenuAction = (rowId: number, action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation();
    closeRowMenu(rowId);
    action();
  };

  const columns = useMemo<DataTableColumn<Project>[]>(() => {
    const profitColor = (row: Project) =>
      (row.summary?.profit ?? 0) >= 0 ? 'success.main' : 'error.main';
    const netCashColor = (row: Project) =>
      (row.summary?.netCash ?? 0) >= 0 ? 'info.main' : 'warning.main';

    // Mobilde 4 finans kolonu tek "Özet" kolonunda (Kâr + Net Nakit) daraltılır
    const financialColumns: DataTableColumn<Project>[] = isMobile
      ? [
          {
            id: 'financials',
            label: t('projects.columns.financials'),
            align: 'right',
            render: (row) => (
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('projects.columns.profit')}
                </Typography>
                {moneyCell(row.summary?.profit, profitColor(row))}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  {t('projects.columns.netCash')}
                </Typography>
                {moneyCell(row.summary?.netCash, netCashColor(row))}
              </Box>
            ),
          },
        ]
      : [
          {
            id: 'revenue',
            label: t('projects.columns.revenue'),
            align: 'right',
            render: (row) => moneyCell(row.summary?.revenue, 'success.main'),
          },
          {
            id: 'cost',
            label: t('projects.columns.cost'),
            align: 'right',
            render: (row) => moneyCell(row.summary?.cost, 'error.main'),
          },
          {
            id: 'profit',
            label: t('projects.columns.profit'),
            align: 'right',
            render: (row) => moneyCell(row.summary?.profit, profitColor(row)),
          },
          {
            id: 'netCash',
            label: t('projects.columns.netCash'),
            align: 'right',
            render: (row) => moneyCell(row.summary?.netCash, netCashColor(row)),
          },
        ];

    return [
    {
      id: 'name',
      label: t('projects.columns.name'),
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountTreeIcon fontSize="small" color="primary" />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{row.name}</Typography>
            {row.description && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {row.description}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: 'code',
      label: t('projects.columns.code'),
      render: (row) => <>{row.code || '-'}</>,
    },
    {
      id: 'account',
      label: t('projects.columns.account'),
      render: (row) => <>{row.accountId ? (accountNames[row.accountId] || `#${row.accountId}`) : '-'}</>,
    },
    ...financialColumns,
    {
      id: 'status',
      label: t('projects.columns.status'),
      align: 'center',
      render: (row) => statusChip(row.status),
    },
    {
      id: 'startDate',
      label: t('projects.columns.startDate'),
      render: (row) => <>{fmtDate(row.startDate)}</>,
    },
    {
      id: 'endDate',
      label: t('projects.columns.endDate'),
      render: (row) => <>{fmtDate(row.endDate)}</>,
    },
    {
      id: 'actions',
      label: t('projects.columns.actions'),
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorElRow((prev) => ({ ...prev, [row.id]: e.currentTarget })); }}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorElRow[row.id]}
            open={Boolean(anchorElRow[row.id])}
            onClose={() => closeRowMenu(row.id)}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={rowMenuAction(row.id, () => setEditTarget(row))}>
              <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
              <ListItemText primary={t('projects.edit')} />
            </MenuItem>
            {row.status !== 'completed' && (
              <MenuItem onClick={rowMenuAction(row.id, () => setStatusTarget({ project: row, status: 'completed' }))}>
                <ListItemIcon><DoneAllIcon fontSize="small" color="success" /></ListItemIcon>
                <ListItemText primary={t('projects.markCompleted')} />
              </MenuItem>
            )}
            {row.status !== 'cancelled' && (
              <MenuItem onClick={rowMenuAction(row.id, () => setStatusTarget({ project: row, status: 'cancelled' }))}>
                <ListItemIcon><CancelIcon fontSize="small" color="warning" /></ListItemIcon>
                <ListItemText primary={t('projects.markCancelled')} />
              </MenuItem>
            )}
            {row.status !== 'active' && (
              <MenuItem onClick={rowMenuAction(row.id, () => setStatusTarget({ project: row, status: 'active' }))}>
                <ListItemIcon><RestartAltIcon fontSize="small" color="primary" /></ListItemIcon>
                <ListItemText primary={t('projects.markActive')} />
              </MenuItem>
            )}
            <MenuItem onClick={rowMenuAction(row.id, () => setDeleteTarget(row))}>
              <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
              <ListItemText primary={t('projects.delete')} />
            </MenuItem>
            <MenuItem onClick={rowMenuAction(row.id, () => setHistoryTarget(row))}>
              <ListItemIcon><HistoryIcon fontSize="small" color="action" /></ListItemIcon>
              <ListItemText primary={t('projects.history')} />
            </MenuItem>
          </Menu>
        </Box>
      ),
    },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorElRow, accountNames, localeCode, t, isMobile, moneyCell]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Başlık */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <AccountTreeIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" component="h1" fontWeight="bold">
          {t('projects.title')}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddModalOpen(true)}>
          {t('projects.addProject')}
        </Button>
        <GuideButton guideKey="projects.guide" color="primary" />
      </Box>

      {/* Filtreler */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              placeholder={t('projects.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel id="project-status-filter-label">{t('projects.columns.status')}</InputLabel>
              <Select
                labelId="project-status-filter-label"
                value={statusFilter}
                label={t('projects.columns.status')}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">{t('projects.statusAll')}</MenuItem>
                <MenuItem value="active">{t('projects.statusActive')}</MenuItem>
                <MenuItem value="completed">{t('projects.statusCompleted')}</MenuItem>
                <MenuItem value="cancelled">{t('projects.statusCancelled')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadProjects}>
              {t('projects.refresh')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <AccountingDataTable<Project>
        columns={columns}
        rows={projects}
        getRowId={(row) => `project-${row.id}`}
        loading={loading}
        serverSidePagination
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={(newPage, newPageSize) => {
          setPage(newPage);
          setPageSize(newPageSize);
        }}
        onRowClick={(row) => navigate(`/projeler/${row.id}`)}
        emptyMessage={search || statusFilter ? t('projects.emptyFiltered') : t('projects.empty')}
        emptyIcon={<AccountTreeIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
        ariaLabel={t('projects.title')}
        columnResizeStorageKey="accounting-projects"
      />

      {/* Ekle / Düzenle */}
      <AddProjectModal
        open={addModalOpen || Boolean(editTarget)}
        editData={editTarget}
        onClose={() => { setAddModalOpen(false); setEditTarget(null); }}
        onSuccess={(isEdit) => {
          setSnackbar(isEdit ? t('projects.updated') : t('projects.created'));
          loadProjects();
        }}
        createdBy={userDetails?.email || undefined}
      />

      {/* Silme onayı */}
      <Dialog open={Boolean(deleteTarget)} onClose={deleting ? undefined : () => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('projects.deleteConfirmTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('projects.deleteConfirmText', { name: deleteTarget?.name || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{t('projects.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting} startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}>
            {t('projects.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Durum geçişi onayı */}
      <Dialog
        open={Boolean(statusTarget)}
        onClose={statusSaving ? undefined : () => setStatusTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('projects.statusChangeTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('projects.statusChangeText', {
              name: statusTarget?.project.name || '',
              status: statusTarget ? t(`projects.status${statusTarget.status === 'active' ? 'Active' : statusTarget.status === 'completed' ? 'Completed' : 'Cancelled'}`) : '',
            })}
          </DialogContentText>
          <DialogContentText variant="caption" sx={{ mt: 1, display: 'block' }}>
            {t('projects.statusChangeHint')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)} disabled={statusSaving}>{t('projects.cancel')}</Button>
          <Button
            variant="contained"
            onClick={handleStatusChange}
            disabled={statusSaving}
            startIcon={statusSaving ? <CircularProgress size={16} /> : <DoneAllIcon />}
          >
            {t('projects.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İşlem Geçmişi */}
      <RecordHistoryModal
        open={Boolean(historyTarget)}
        onClose={() => setHistoryTarget(null)}
        entityType="Project"
        entityId={historyTarget?.id ?? null}
        recordLabel={historyTarget?.name}
        fieldLabels={PROJECT_HISTORY_FIELD_LABELS}
      />

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar(null)}>{snackbar}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Projects;
